import React, { useState } from 'react';
import { makeStyles, createStyles, Theme } from '@material-ui/core/styles';
import { Fade, FormControl, Icon, InputAdornment, InputLabel, Popper, Select } from '@material-ui/core';
import ValidationEvent from 'components/ui/form/ValidationEvent';
import ValidationEventImpl from 'components/ui/form/ValidationEventImpl';
import PasswordStrengthBar from 'components/ui/form/PasswordStrengthBar';
import clsx from 'clsx';

let ValidatedSelect = (props: any) => {
  const useStyles = makeStyles((theme: Theme) =>
    createStyles({
      arrowPopper: {
        backgroundColor: 'rgba(0, 0, 0, 0.54)',
        color: '#FFFFFF',
        zIndex: 1,
        '&[x-placement*="bottom"] .arrow': {
          width: 0,
          height: 0,
          borderLeft: '1em solid transparent',
          borderRight: '1em solid transparent',
          borderBottom: '1em solid rgba(0, 0, 0, 0.54)',
          marginTop: '-0.9em',
        
          '&:before': {
            borderWidth: '0 1em 1em 1em',
            borderColor: 'transparent transparent rgba(0, 0, 0, 0.54) transparent',
          }
        },

        '&[x-placement*="top"] .arrow': {
          bottom: 0,
          width: 0,
          height: 0,
          borderLeft: '1em solid transparent',
          borderRight: '1em solid transparent',
          borderTop: '1em solid rgba(0, 0, 0, 0.54)',
          marginBottom: '-0.9em',

          '&:before': {
            borderWidth: '1em 1em 0 1em',
            borderColor: 'rgba(0, 0, 0, 0.54) transparent transparent transparent',
          }
        },

        '&[x-placement*="right"] .arrow': {
          left: 0,
          width: 0, 
          height: 0, 
          borderTop: '1em solid transparent',
          borderBottom: '1em solid transparent',
          borderRight: '1em solid rgba(0, 0, 0, 0.54)',
          marginLeft: '-0.9em',

          '&:before': {
            borderWidth: '1em 1em 1em 0',
            borderColor: 'transparent rgba(0, 0, 0, 0.54) transparent transparent',
          }
        },

        '&[x-placement*="left"] .arrow': {
          
          right: 0,
          width: 0, 
          height: 0, 
          borderTop: '1em solid transparent',
          borderBottom: '1em solid transparent',
          borderLeft: '1em solid rgba(0, 0, 0, 0.54)',
          marginRight: '-0.9em',

          '&:before': {
            borderWidth: '1em 0 1em 1em',
            borderColor: 'transparent transparent transparent rgba(0, 0, 0, 0.54)',
          }
        }
      },      
      arrow: {
        position: 'absolute',
        fontSize: '7px',
        width: '3em',
        height: '3em',
      },   
      errorMessageContainer: {
        position: 'relative',
      },     
      buttonIcon: {
        marginRight: '0.33em',
      },
      textField: {
        marginLeft: theme.spacing(1),
        marginRight: theme.spacing(1),
        width: '90%'
      },
      dense: {
        marginTop: '12px',
      },
      paper: {
        border: '1px solid',
        padding: theme.spacing(1),
        // backgroundColor: theme.palette.background.paper,
      },
      validField: {
      },
      invalidField: {
        backgroundColor: '#FFcccc'
      } 
    })
  );
  const classes = useStyles();
  
  let invalidIcon = <Icon color = 'secondary' className = { clsx(classes.buttonIcon,  'fa fa-exclamation-circle') } />;
  let validIcon = <Icon color = 'primary' className = { clsx(classes.buttonIcon,  'fa fa-check-circle') } />;
  
  const [isValid, setIsValid] = useState<boolean>(true);
  const [isDirty, setIsDirty] = useState<boolean>(false);
  const [errorMessages, setErrorMessages] = useState<any>({});
  const [showErrorMessage, setShowErrorMessage] = useState<boolean>(false);
  const [el, setEl] = React.useState<null | any>(null);
  const [arrowRef, setArrowRef] = React.useState<HTMLSpanElement | null>(null);
  
  const validate = (event: ValidationEvent<HTMLInputElement>) => {
    if(props.beforeValidate) {
      event.isValid = isValid;
      props.beforeValidate(event);
    }
    let newIsValid = true;
    let errorMessages: any = {};
    if(props.required) {
      if(!required(event)) {
        errorMessages.required = `This is a required field.`;
        newIsValid = false;
      }
    }

    if(props.minSelections) {
      if(!minSelections(event, props.minSelections)) {
        errorMessages.minSelections = `Must select at least ${props.minSelections} options.`;
        newIsValid = false;
      }
    }
    
    if(props.maxSelections) {
      if(!maxSelections(event, props.maxSelections)) {
        errorMessages.maxSelections = `Cannot select more than ${props.maxSelections} options.`;
        newIsValid = false;
      }
    }    

    setIsValid(newIsValid);
    setErrorMessages(errorMessages);
    if(props.afterValidate) {
      event.isValid = newIsValid;
      props.afterValidate(event);
    }
    return newIsValid;
  }

  const required = (event: ValidationEvent<HTMLInputElement>): boolean => {
    let isValid =
      event.target.value !== undefined &&
      event.target.value !== null &&
      event.target.value.length > 0;
    return isValid;
  }

  const minSelections = (event: ValidationEvent<{name?: string, value: unknown}>, selections: number): boolean => {
    let value = event.target.value;
    let isValid =
      value === undefined ||
      value === null ||
      !Array.isArray(value) ||
      value.length === 0 ||
      value.length >= selections;
    return isValid;
  }

  const maxSelections = (event: ValidationEvent<{name?: string, value: unknown}>, selections: number): boolean => {
    let value = event.target.value;
    let isValid =
      value === undefined ||
      value === null ||
      !Array.isArray(value) ||
      value.length === 0 ||
      value.length <= selections;
    return isValid;
  }

  const onBlur = (event: React.FocusEvent<{name?: string, value:unknown}>) => {
    // let validationEvent = new ValidationEventImpl<HTMLInputElement>(event.target);
    setIsDirty(true);
    // setShowErrorMessage(!validate(validationEvent));
    if(props.onBlur) {
      props.onBlur(event);
    } 
  }

  const onChange = (event: React.ChangeEvent<{name?: string, value:unknown}>) => {
    // let validationEvent = new ValidationEventImpl<HTMLInputElement>(event.target);
    setIsDirty(true);
    // setShowErrorMessage(!validate(validationEvent));
    if(props.onChange) {
      props.onChange(event);
    } 
  }
    
  let selectProps = Object.assign({}, props);
  delete selectProps.InputProps;
  delete selectProps.beforeValidate;
  delete selectProps.afterValidate;
  delete selectProps.minSelections;
  delete selectProps.maxSelections;
  
  return (
    <FormControl variant="outlined" margin="dense" className = { clsx(classes.textField, classes.dense, isValid ? classes.validField : classes.invalidField, props.className) } fullWidth>
      <InputLabel id={props.labelId}>{props.label}</InputLabel>
      <Select
        { ...selectProps }
        ref = {el => { setEl(el) }}
        onChange={onChange}
        label={props.label}
        MenuProps={{variant:'menu'}}
      >
        {props.children}
      </Select>
      { showErrorMessage && 
        <span className = { classes.errorMessageContainer }>
          <Popper
            className = { classes.arrowPopper }
            open = { showErrorMessage }
            anchorEl = { el }
            placement = 'right'
            modifiers={{
              preventOverflow: {
                enabled: true,
                boundariesElement: 'scrollParent',
              },
              arrow: {
                enabled: Boolean(arrowRef),
                element: arrowRef || {},
              },
            }}
            transition>
              {({ TransitionProps }) => (
                <Fade { ...TransitionProps } timeout={350}>
                  <div>
                    <span className = { clsx("arrow", classes.arrow) } ref = { setArrowRef } />
                    <div className={clsx(classes.paper)}>{ Object.values(errorMessages).join('') }</div>
                  </div>
                </Fade>
              )}
            
          </Popper>
          
        </span>
        
      }      
    </FormControl> 
  )
}

export default ValidatedSelect;